import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { NAV } from "@/lib/site";
import Logo from "@/components/Logo";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  return (
    <>
      <motion.header
        initial={{ y: -80, x: "-50%", opacity: 0 }}
        animate={{ y: 0, x: "-50%", opacity: 1 }}
        transition={{ duration: 0.8, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
        className="fixed left-1/2 top-4 z-[9000] w-[94%] max-w-6xl"
        data-testid="main-navbar"
      >
        <div
          className={`flex items-center justify-between rounded-full px-4 py-2.5 pl-5 transition-all duration-500 ${
            scrolled
              ? "glass shadow-[0_8px_32px_rgba(0,0,0,0.5)]"
              : "border border-transparent bg-transparent"
          }`}
        >
          <Link to="/" className="flex min-w-0 shrink items-center gap-2.5" data-testid="nav-logo">
            <Logo size="md" />
          </Link>

          <nav className="hidden items-center gap-1 md:flex">
            {NAV.map((n) => {
              const active = pathname === n.to;
              return (
                <Link
                  key={n.to}
                  to={n.to}
                  data-testid={`nav-link-${n.label.toLowerCase()}`}
                  className={`relative rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                    active ? "text-slate-900" : "text-slate-600 hover:text-slate-900"
                  }`}
                >
                  {active && (
                    <motion.span
                      layoutId="nav-pill"
                      className="absolute inset-0 rounded-full bg-slate-900/[0.06] ring-1 ring-slate-900/10"
                      transition={{ type: "spring", stiffness: 400, damping: 32 }}
                    />
                  )}
                  <span className="relative z-10">{n.label}</span>
                </Link>
              );
            })}
          </nav>

          <div className="flex shrink-0 items-center gap-2">
            <Link
              to="/contact"
              data-testid="nav-cta"
              className="glossy-btn hidden items-center gap-1.5 rounded-full px-5 py-2.5 text-sm font-semibold text-white transition-transform duration-300 hover:scale-[1.03] md:flex"
            >
              Start a project <ArrowUpRight size={15} />
            </Link>
            <button
              onClick={() => setOpen((o) => !o)}
              className="glass flex h-10 w-10 items-center justify-center rounded-full text-slate-900 md:hidden"
              data-testid="mobile-menu-toggle"
              aria-label="Menu"
            >
              {open ? <X size={18} /> : <Menu size={18} />}
            </button>
          </div>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -12, x: "-50%" }}
            animate={{ opacity: 1, y: 0, x: "-50%" }}
            exit={{ opacity: 0, y: -12, x: "-50%" }}
            className="fixed left-1/2 top-20 z-[8999] w-[94%] max-w-6xl md:hidden"
            data-testid="mobile-menu"
          >
            <div className="glass flex flex-col gap-1 rounded-3xl p-4">
              {NAV.map((n) => (
                <Link
                  key={n.to}
                  to={n.to}
                  data-testid={`mobile-nav-link-${n.label.toLowerCase()}`}
                  className={`rounded-2xl px-4 py-3 text-base font-medium ${
                    pathname === n.to ? "bg-slate-900/[0.06] text-slate-900" : "text-slate-700"
                  }`}
                >
                  {n.label}
                </Link>
              ))}
              <Link
                to="/contact"
                className="glossy-btn mt-1 rounded-2xl px-4 py-3 text-center text-base font-semibold text-white"
              >
                Start a project
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
